import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { callErrorToast } from "../../../utilities/index.js";
import { ContractsService } from "../../../services/contract/contracts.ts";

const contractsService = new ContractsService();

interface DeleteContractProviderProps {
  children: ReactNode;
  data?: string[];
  callback?: () => void;
}

interface DeleteContractContextValue {
  loader: boolean;
  deleteContracts: () => void;
  data: string[];
  callback?: () => void;
}

const DeleteContractContext = createContext<
  DeleteContractContextValue | undefined
>(undefined);

const DeleteContractProvider: React.FC<DeleteContractProviderProps> = ({
  children,
  data = [],
  callback,
}) => {
  const [error, setError] = useState<unknown>(null);
  const [loader, setLoader] = useState<boolean>(false);

  const deleteContracts = (): void => {
    setLoader(true);
    const promises = data.map((id) => contractsService.delete(id));
    Promise.all(promises)
      .then(() => {
        setLoader(false);
        if (callback) {
          callback();
        }
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  return (
    <DeleteContractContext.Provider
      value={{ loader, deleteContracts, data, callback }}
    >
      {children}
    </DeleteContractContext.Provider>
  );
};

const useDeleteContractContext = (): DeleteContractContextValue => {
  const context = useContext(DeleteContractContext);
  if (!context) {
    throw new Error(
      "useDeleteContractContext must be used within a DeleteContractProvider"
    );
  }
  return context;
};

export { DeleteContractProvider, useDeleteContractContext };
